import ModuleFactory, {
  MainModule,
  Interval,
  Intervals,
  IntervalTypesVector,
} from "./hg.js";
import { HGInterval, HGIntervalType, HGSpan } from "../common/types.ts";
import { constructInterval } from "@/common/intervals.ts";
import { getParentType } from "@/common/intervalTypes.ts";

let cachedModule: MainModule | null = null;

export function IntervalsRange(
  interval: HGInterval,
  end_interval: HGInterval | null,
  it: HGIntervalType,
): [bigint, bigint] {
  const { begin, end } = MapIntervals(interval, end_interval, it);
  return [begin.n, end.n];
}

export function SplitIntervals(
  interval: HGInterval,
  endInterval: HGInterval | null,
  it: HGIntervalType,
) {
  const span = MapIntervals(interval, endInterval, it);
  const intervals: HGInterval[] = [];
  for (let n = span.begin.n; n <= span.end.n; ++n) {
    intervals.push({
      n,
      it,
    });
  }

  return intervals;
}

export function MapInterval(
  interval: HGInterval,
  it: HGIntervalType,
  tt?: HGIntervalType,
): HGSpan {
  return GetSpans(interval, null, [it], tt)[it];
}

export function ConvertInterval(interval: HGInterval, it: HGIntervalType) {
  const span = MapInterval(interval, it);
  return { n: span.begin.n, it };
}

// Nested by interval type -> n -> end interval type -> end n -> requested
// type, terminating in a cached HGSpan. `unknown` + casts stand in for `any`
// here because the depth genuinely varies by branch (no end interval vs.
// one).
const intervalsCache: Record<string, unknown> = {};

export function MapIntervals(
  interval: HGInterval,
  end_interval: HGInterval | null,
  it: HGIntervalType,
): HGSpan {
  let lastRef = intervalsCache;

  if (!(interval.it in lastRef)) {
    lastRef[interval.it] = {};
  }
  lastRef = lastRef[interval.it] as Record<string, unknown>;

  if (!(interval.n.toString() in lastRef)) {
    lastRef[interval.n.toString()] = {};
  }
  lastRef = lastRef[interval.n.toString()] as Record<string, unknown>;

  if (!end_interval) {
    if (!("" in lastRef)) {
      lastRef[""] = {};
    }
    lastRef = lastRef[""] as Record<string, unknown>;
  } else {
    if (!(end_interval.it in lastRef)) {
      lastRef[end_interval.it] = {};
    }
    lastRef = lastRef[end_interval.it] as Record<string, unknown>;

    if (!(end_interval.n.toString() in lastRef)) {
      lastRef[end_interval.n.toString()] = {};
    }
    lastRef = lastRef[end_interval.n.toString()] as Record<string, unknown>;
  }

  if (it in lastRef) {
    return lastRef[it] as HGSpan;
  } else {
    const spans = GetSpans(interval, end_interval, [it])[it];
    lastRef[it] = spans;
    return spans;
  }
}

export function GetParentInterval(
  interval: HGInterval,
  it?: HGIntervalType,
): HGInterval {
  if (!it) {
    it = getParentType(interval.it);
  }
  if (!it) throw Error;
  const intervals = MapInterval(interval, it);
  return constructInterval(intervals.begin.n, it);
}

// misc.

export async function initModule() {
  cachedModule = await ModuleFactory({
    locateFile: (path: string) => {
      if (path.endsWith(".wasm")) {
        return `${window.location.origin}/${path}`;
      }
      return path;
    },
  });
}

export function GetSpans(
  i1: HGInterval,
  i2: HGInterval | null = null,
  its: HGIntervalType[] | null = null,
  tt?: HGIntervalType,
): Record<HGIntervalType, HGSpan> {
  if (!cachedModule) {
    throw Error("Wasm module is not loaded!");
  }

  const module = cachedModule;

  const interval_1: Interval = {
    n: i1.n < 0 ? -i1.n : i1.n,
    it: i1.it,
    era: i1.n < 0 ? module.Era.BC : module.Era.AD,
  };

  let interval_2: Interval | undefined = undefined;
  if (i2) {
    interval_2 = {
      n: i2.n < 0 ? -i2.n : i2.n,
      it: i2.it,
      era: i2.n < 0 ? module.Era.BC : module.Era.AD,
    };
  }

  let filter: IntervalTypesVector | undefined = undefined;
  if (its) {
    filter = new module.IntervalTypesVector();
    for (let i = 0; i < its.length; ++i) {
      filter.push_back(its[i]);
    }
  }

  const mappings = module.MapIntervals(interval_1, interval_2, tt, filter);

  const mappingKeys = mappings.keys();

  let result: Record<HGIntervalType, HGSpan> = {};
  for (let i = 0; i < mappingKeys.size(); ++i) {
    const intervalType = mappingKeys.get(i) as HGIntervalType;
    const intervals: Intervals | undefined = mappings.get(intervalType);
    if (!intervals) continue;
    const span: HGSpan = {
      begin: {
        ...intervals.begin,
        tt: intervals.begin.tt.toString(),
      },
      end: {
        ...intervals.end,
        tt: intervals.end.tt.toString(),
      },
    };
    if (i1.n < 0) {
      span.begin.n = -span.begin.n;
      if (!i2 || i2.n < 0) {
        span.end.n = -span.end.n;
      }
    }

    result[intervalType] = span;
  }
  mappings.delete();
  filter?.delete();

  return result;
}
